import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { setDisplayCurrency } from "@/lib/money";
import { useAuth } from "@/context/AuthContext";

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const [locale, setLocale] = useState({
    currency: "GBP", country: "GB", rates: null,
    paymentProvider: "paypal_stripe", paymentNote: null,
  });

  useEffect(() => {
    const saved = window.localStorage.getItem("zuntria_currency");
    if (saved) setDisplayCurrency(saved);
    api.get("/locale/detect").then(({ data }) => {
      const currency = saved || data.currency || "GBP";
      setDisplayCurrency(currency);
      setLocale({
        currency,
        country: data.country_code,
        rates: data.rates,
        paymentProvider: data.region_payment_provider,
        paymentNote: data.payment_note,
      });
    }).catch(() => {
      if (saved) setLocale((l) => ({ ...l, currency: saved }));
    });
  }, []);

  // Profile language wins over browser (but not over the user's manual pick)
  useEffect(() => {
    const manual = window.localStorage.getItem("zuntria_lang");
    if (!manual && user?.preferred_ui_language && user.preferred_ui_language !== i18n.language) {
      i18n.changeLanguage(user.preferred_ui_language);
    }
  }, [user, i18n]);

  const setCurrency = useCallback((c) => {
    window.localStorage.setItem("zuntria_currency", c);
    setDisplayCurrency(c);
    setLocale((l) => ({ ...l, currency: c }));
  }, []);

  const setLanguage = useCallback((code) => {
    window.localStorage.setItem("zuntria_lang", code);
    i18n.changeLanguage(code);
    if (user) api.put("/profile/preferences", { preferred_ui_language: code }).catch(() => {});
  }, [i18n, user]);

  const convertFromGBP = useCallback((n) => {
    const r = locale.rates?.[locale.currency];
    return r && locale.currency !== "GBP" ? n * r : null;
  }, [locale.rates, locale.currency]);

  const value = useMemo(
    () => ({ ...locale, setCurrency, setLanguage, convertFromGBP }),
    [locale, setCurrency, setLanguage, convertFromGBP]
  );
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export const useLocale = () => useContext(LocaleContext);
